import { useRef, useEffect, useState } from "react";
import { Routes, Route, useParams, useNavigate, Link } from "react-router-dom";
import { RankingCard, ShareActions, BackgroundCardGallery, FeedbackDialog, TestControlDialog, RankingsPage, DEFAULT_TRANSFORM } from "./components";
import type { CardTransform } from "./components";
import { RankingProvider, useRankingContext, AppModeProvider, useAppMode } from "./hooks";
import type { RankingState } from "./hooks";
import type { Ranking } from "@pick-three-ai/types";
import type { LLMModel } from "@pick-three-ai/constants";
import { RotateCcw, Loader2, MessageSquare, BarChart3, Eye } from "lucide-react";
import { analytics } from "./lib/analytics";

// API base URL - in production this would be from env
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8787";

// Check if we're in development mode
const isDev = import.meta.env.DEV;

function App() {
  return (
    <AppModeProvider>
      <RankingProvider>
        <BackgroundCardGallery />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/rankings" element={<RankingsPage />} />
          <Route path="/u/:handle" element={<PublicCardView />} />
          <Route path="/card/:id" element={<PublicCardView />} />
        </Routes>
      </RankingProvider>
    </AppModeProvider>
  );
}

function HomePage() {
  const cardRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isTestControlOpen, setIsTestControlOpen] = useState(false);
  const [cardTransform, setCardTransform] = useState<CardTransform>(DEFAULT_TRANSFORM);
  const { isGeneric } = useAppMode();
  const {
    rankings,
    setModel,
    setBlurb,
    title,
    setTitle,
    handle,
    setHandle,
    isComplete,
    clearRankings,
    hasAnySelection,
    remixingFrom,
    clearRemixingFrom,
  } = useRankingContext();

  // Track page view on mount
  useEffect(() => {
    analytics.pageView("home");
  }, []);

  const handleStartOver = () => {
    const modelsSelected = rankings.filter((r) => r.model !== null).length;
    analytics.startOverClicked(modelsSelected);
    clearRankings();
    clearRemixingFrom();
  };
  
  const handleSeeOriginal = () => {
    if (remixingFrom) {
      navigate(`/card/${remixingFrom.shortCode}`);
    }
  };

  return (
    <main className="h-full flex flex-col">
      {/* Page Header */}
      <header className="flex-shrink-0 pt-6 sm:pt-8 pb-2 text-center">
        <h1 
          className={`text-3xl sm:text-4xl md:text-6xl font-normal italic tracking-tight text-gradient font-display ${isDev ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}`}
          onClick={isDev ? () => setIsTestControlOpen(true) : undefined}
          title={isDev ? "Open test controls" : undefined}
        >
          {isGeneric ? "Pick Three" : "Pick Three AI"}
        </h1>
        <Link
          to="/rankings"
          onClick={() => analytics.rankingsPageClicked()}
          className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <BarChart3 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
          {isGeneric ? "See Rankings" : "See Model Rankings"}
        </Link>
      </header>

      {/* Main Content - Centered */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-4 sm:py-6">
        <div className="w-full max-w-md space-y-4 sm:space-y-5">
          {/* See Original - shown when remixing */}
          {remixingFrom && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={handleSeeOriginal}
                className="flex items-center gap-2 px-3 py-1.5 md:px-5 md:py-2.5 text-xs md:text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <Eye className="w-3 h-3 md:w-4 md:h-4" />
                See original
              </button>
            </div>
          )}

          {/* Ranking Card */}
          <RankingCard
            ref={cardRef}
            rankings={rankings}
            onModelChange={setModel}
            onBlurbChange={setBlurb}
            title={title}
            onTitleChange={setTitle}
            handle={handle}
            onHandleChange={setHandle}
            transform={isDev ? cardTransform : undefined}
          />

          {/* Share Actions */}
          {!(isDev && cardTransform.hideBottomControls) && (
            <ShareActions
              rankings={rankings}
              isComplete={isComplete}
              title={title}
              handle={handle}
              cardRef={cardRef}
              remixedFromId={remixingFrom?.shortCode}
            />
          )}

          {/* Action Buttons Row - Start Over + Feedback */}
          {!(isDev && cardTransform.hideBottomControls) && (
            <div className="flex items-center justify-center gap-6">
              {hasAnySelection && (
                <button
                  type="button"
                  onClick={handleStartOver}
                  className="flex items-center gap-2 px-3 py-1.5 md:px-5 md:py-2.5 text-xs md:text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <RotateCcw className="w-3 h-3 md:w-4 md:h-4" />
                  Start over
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsFeedbackOpen(true)}
                className="flex items-center gap-2 px-3 py-1.5 md:px-5 md:py-2.5 text-xs md:text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <MessageSquare className="w-3 h-3 md:w-4 md:h-4" />
                Share feedback
              </button>
            </div>
          )}

          {/* Feedback Dialog */}
          <FeedbackDialog
            isOpen={isFeedbackOpen}
            onClose={() => setIsFeedbackOpen(false)}
          />

          {/* Test Control Dialog (dev only) */}
          {isDev && (
            <TestControlDialog
              isOpen={isTestControlOpen}
              onClose={() => setIsTestControlOpen(false)}
              transform={cardTransform}
              onTransformChange={setCardTransform}
            />
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="flex-shrink-0 py-3 sm:py-4 text-center">
      </footer>
    </main>
  );
}

function PublicCardView() {
  const { id: shortCode } = useParams<{ handle?: string; id?: string }>();
  const navigate = useNavigate();
  const { setRankings, setTitle, setHandle, remixingFrom, setRemixingFrom } = useRankingContext();
  const { isGeneric } = useAppMode();
  
  const [ranking, setRanking] = useState<Ranking | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const appName = isGeneric ? "Pick Three" : "Pick Three AI";
  
  // Check if user is currently remixing this ranking
  const isRemixingThisRanking = remixingFrom?.shortCode === shortCode;

  // Fetch ranking data from API
  const fetchRanking = async () => {
    if (!shortCode) {
      setError("No card ID provided");
      setIsLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/rankings/${shortCode}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError("Card not found");
        } else {
          setError("Failed to load card");
        }
        setIsLoading(false);
        return;
      }
      
      const data: Ranking = await response.json();
      setRanking(data);
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Failed to load card");
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchRanking();
  }, [shortCode]);
  
  // Focus-aware polling for remix count updates
  useEffect(() => {
    if (!shortCode || isLoading || error) return;
    
    let intervalId: ReturnType<typeof setInterval> | null = null;
    
    const startPolling = () => {
      if (intervalId) return;
      intervalId = setInterval(() => {
        if (document.hasFocus()) {
          fetchRanking();
        }
      }, 5000); // Poll every 5 seconds when focused
    };
    
    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        startPolling();
      } else {
        stopPolling();
      }
    };
    
    // Start polling if page is visible
    if (document.visibilityState === "visible") {
      startPolling();
    }
    
    document.addEventListener("visibilitychange", handleVisibilityChange);
    
    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [shortCode, isLoading, error]);

  // Update document title when ranking loads
  useEffect(() => {
    if (ranking) {
      const title = ranking.title || "Best Overall";
      const handle = ranking.handle ? `@${ranking.handle}` : "";
      document.title = `${title}${handle ? ` by ${handle}` : ""} — ${appName}`;
    }
    return () => {
      const tagline = isGeneric 
        ? "Rank your top three. Share with everyone."
        : "Your Top Models. Shared in Seconds.";
      document.title = `${appName} — ${tagline}`;
    };
  }, [ranking, appName, isGeneric]);

  // Convert ranking to display format
  const displayRankings: RankingState | null = ranking ? [
    { model: ranking.rank1 as LLMModel, blurb: ranking.blurb1 || "" },
    { model: ranking.rank2 as LLMModel, blurb: ranking.blurb2 || "" },
    { model: ranking.rank3 as LLMModel, blurb: ranking.blurb3 || "" },
  ] : null;

  const handleRemix = () => {
    if (!displayRankings || !ranking || !shortCode) return;
    
    analytics.remixClicked(shortCode);
    
    // Load only models and title into the editor (clear blurbs and handle)
    const remixRankings: RankingState = [
      { model: ranking.rank1 as LLMModel, blurb: "" },
      { model: ranking.rank2 as LLMModel, blurb: "" },
      { model: ranking.rank3 as LLMModel, blurb: "" },
    ];
    setRankings(remixRankings);
    setTitle(ranking.title || "Best Overall");
    setHandle(""); // Clear handle for remix
    setRemixingFrom(ranking); // Store the original ranking being remixed
    navigate("/");
  };
  
  const handleSeeYourRemix = () => {
    navigate("/");
  };

  // Loading state
  if (isLoading) {
    return (
      <main className="h-full flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center px-4 py-6 sm:py-8">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </main>
    );
  }

  // Error state
  if (error || !displayRankings || !ranking) {
    return (
      <main className="h-full flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center px-4 py-6 sm:py-8">
          <div className="text-center space-y-4">
            <p className="text-muted-foreground">{error || "Card not found"}</p>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="px-4 py-2 rounded-lg glass hover:bg-[rgba(255,255,255,0.08)] text-sm font-medium transition-colors"
            >
              Create your own
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="h-full flex flex-col">
      {/* Page Header */}
      <header className="flex-shrink-0 pt-6 sm:pt-8 pb-2 text-center">
        <Link 
          to="/"
          className="block text-3xl sm:text-4xl md:text-6xl font-normal italic tracking-tight text-gradient font-display hover:opacity-80 transition-opacity"
        >
          {appName}
        </Link>
        <Link
          to="/rankings"
          onClick={() => analytics.rankingsPageClicked()}
          className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <BarChart3 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
          {isGeneric ? "See Rankings" : "See Model Rankings"}
        </Link>
      </header>

      {/* Main Content - Centered */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-4 sm:py-6">
        <div className="w-full max-w-md space-y-4 sm:space-y-5">
          {/* Reuse the same RankingCard component in read-only mode */}
          <RankingCard
            rankings={displayRankings}
            onModelChange={() => {}}
            onBlurbChange={() => {}}
            title={ranking.title || "Best Overall"}
            handle={ranking.handle || ""}
            isReadOnly={true}
          />

          {/* Remix count display */}
          {ranking.remixCount !== undefined && (
            <div className="flex justify-center">
              <span className="text-xs sm:text-sm text-muted-foreground">
                {ranking.remixCount} {ranking.remixCount === 1 ? "remix" : "remixes"}
              </span>
            </div>
          )}

          {/* Remix CTA or See your remix button */}
          <div className="flex justify-center">
            {isRemixingThisRanking ? (
              <button
                type="button"
                onClick={handleSeeYourRemix}
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl glass hover:bg-[rgba(255,255,255,0.08)] font-semibold text-sm sm:text-base transition-all duration-200"
              >
                <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5" />
                See your remix
              </button>
            ) : (
              <button
                type="button"
                onClick={handleRemix}
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-accent hover:bg-accent-hover text-accent-foreground font-semibold text-sm sm:text-base transition-all duration-200 glow-accent"
              >
                <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5" />
                Remix
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="flex-shrink-0 py-3 sm:py-4 text-center">
      </footer>
    </main>
  );
}

export default App;

